import { useEffect, useState } from 'react';
import {
  getModelSettings,
  putModelSettings,
  type ModelSettings,
} from '@/services/modelsApi';
import { toast } from '@/state/toastStore';

/**
 * Admin form for picking which Vertex model IDs the studio uses. Backed by
 * the app_settings table (or its localStorage mirror in local mode).
 *
 * Changes apply to the next generation — `AppSetting::get` is cached and
 * busted on PUT, so propagation is sub-second across PHP-FPM workers using
 * a shared cache driver.
 */
export function ModelsAdmin() {
  const [loaded, setLoaded] = useState(false);
  const [draft, setDraft] = useState<ModelSettings>({
    imageModel: '',
    videoFastModel: '',
    videoFullModel: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void getModelSettings()
      .then((s) => {
        if (cancelled) return;
        setDraft(s);
        setLoaded(true);
      })
      .catch((err) => {
        toast.err(err instanceof Error ? err.message : 'Load failed');
      });
    return () => { cancelled = true; };
  }, []);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const saved = await putModelSettings(draft);
      setDraft(saved);
      toast.ok('Models updated. Applies to next generation.');
    } catch (err) {
      toast.err(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (!loaded) {
    return <p className="font-mono text-[12px] text-text-4">loading…</p>;
  }

  return (
    <form onSubmit={save} className="space-y-4 rounded-md bg-surface-1 p-5">
      <ModelInput
        label="Image model"
        hint="Used by every image generation. Default: gemini-3-pro-image-preview."
        value={draft.imageModel}
        onChange={(v) => setDraft((d) => ({ ...d, imageModel: v }))}
      />
      <ModelInput
        label="Video model — fast"
        hint="Used when scene-detail picks the 'fast' tier. Default: veo-3.1-fast-generate-preview."
        value={draft.videoFastModel}
        onChange={(v) => setDraft((d) => ({ ...d, videoFastModel: v }))}
      />
      <ModelInput
        label="Video model — full"
        hint="Used for the 'full' tier (higher quality, slower). Default: veo-3.1-generate-preview."
        value={draft.videoFullModel}
        onChange={(v) => setDraft((d) => ({ ...d, videoFullModel: v }))}
      />
      <button
        type="submit"
        disabled={saving}
        className="rounded-md bg-accent px-4 py-2 text-[13px] font-medium text-text-on-accent transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
      >
        {saving ? 'Saving…' : 'Save models'}
      </button>
      <p className="text-[11px] text-text-4">
        Tip: paste a model ID from the Vertex AI catalog. Empty values fall
        back to the deploy-time env vars.
      </p>
    </form>
  );
}

function ModelInput({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium uppercase tracking-[0.14em] text-text-3">
        {label}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="vertex model id"
        className="w-full rounded-md bg-surface-2 px-3 py-2 font-mono text-[12px] text-text-1 placeholder:text-text-4 outline-none ring-1 ring-line-2 focus:ring-line-3"
      />
      <span className="mt-1 block text-[11px] text-text-4">{hint}</span>
    </label>
  );
}
